import { useMemo } from 'react';

import { ServiceTypeItem } from '@/app/(app)/settings/service_plans/[id]/tab-service-types';
import { useServiceTypeTemplateCtx } from '@/app/(app)/settings/service_types/[id]/provider';
import { ButtonEmpty } from '@/components/atoms/button-empty';
import { useServiceTypeTemplatesCtx } from '@/components/providers/service-templates-provider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

export function ServiceTypeTemplateReschedule() {
  const { serviceTypeTemplate, update } = useServiceTypeTemplateCtx();
  const { serviceTypeTemplates: allServiceTypeTemplates } = useServiceTypeTemplatesCtx();

  const [currentServiceTypes, availableServiceTypes] = useMemo(() => {
    return [
      allServiceTypeTemplates
        .filter((st) => serviceTypeTemplate.reschedules_service_type_ids?.includes(st.id))
        .toSorted((a, b) => a.name.localeCompare(b.name)),
      allServiceTypeTemplates
        .filter((st) => st.status === 'ACTIVE' && !serviceTypeTemplate.reschedules_service_type_ids?.includes(st.id))
        .toSorted((a, b) => a.name.localeCompare(b.name)),
    ];
  }, [allServiceTypeTemplates, serviceTypeTemplate.reschedules_service_type_ids]);

  const addServiceType = (serviceTypeId: string) => {
    const prevIds = serviceTypeTemplate.reschedules_service_type_ids ?? [];
    const newIds = Array.from(new Set([...prevIds, serviceTypeId]));
    update({
      reschedules_service_type_ids: newIds,
    });
  };

  const removeServiceType = (serviceTypeId: string) => {
    const prevIds = serviceTypeTemplate.reschedules_service_type_ids ?? [];
    const newIds = prevIds.filter((id) => id !== serviceTypeId);
    update({
      reschedules_service_type_ids: newIds,
    });
  };

  return (
    <div className='flex w-full flex-col gap-8'>
      <div className='flex flex-col gap-1'>
        <h2 className='text-heading-md'>Reschedule Settings</h2>
        <p className='text-sm text-muted-foreground'>Configure reschedule settings for this service type.</p>
      </div>
      <div className='flex flex-col gap-6'>
        <div className='y-4 flex w-full items-center justify-between'>
          <div>
            <Label>Can be rescheduled?</Label>
            <span className='text-sm text-muted-foreground'>
              Allow customers or staff to move appointments of this service type.
            </span>
          </div>
          <Switch
            id='reschedule-allowed'
            checked={serviceTypeTemplate.reschedule_allowed}
            onCheckedChange={(checked) => {
              update({ reschedule_allowed: checked });
            }}
          />
        </div>

        <div className='flex w-full items-center justify-between gap-6'>
          <div>
            <Label>Reschedule window (days)</Label>
            <span className='text-sm text-muted-foreground'>
              Maximum number of days appointments of this service type can be moved, either earlier or later.
            </span>
          </div>
          <div>
            <Input
              className='col-span-2'
              type='number'
              min='0'
              value={serviceTypeTemplate.reschedule_move_days ?? 0}
              disabled={!serviceTypeTemplate.reschedule_allowed}
              onChange={(e) => {
                const parsed = parseInt(e.target.value || '0', 10);
                update({
                  reschedule_move_days: Math.max(0, Number.isNaN(parsed) ? 0 : parsed),
                });
              }}
            />
          </div>
        </div>

        <div className='flex w-full items-center justify-between'>
          <div>
            <Label>Require same month?</Label>
            <span className='text-sm text-muted-foreground'>
              Restrict reschedules to stay within the same calendar month.
            </span>
          </div>
          <Switch
            id='reschedule-require-same-month'
            checked={serviceTypeTemplate.reschedule_require_same_month}
            onCheckedChange={(checked) => {
              update({
                reschedule_require_same_month: checked,
              });
            }}
            disabled={!serviceTypeTemplate.reschedule_allowed}
          />
        </div>

        <div className='flex w-full flex-col justify-between gap-3'>
          <div>
            <Label>Reschedulable to</Label>
            <span className='text-sm text-muted-foreground'>
              Choose which other service types this one can be rescheduled into.
            </span>
          </div>

          <div className='flex items-center gap-3'>
            <Select onValueChange={addServiceType} disabled={!serviceTypeTemplate.reschedule_allowed}>
              <SelectTrigger className='flex-1'>
                <SelectValue placeholder='Add service type' />
              </SelectTrigger>
              <SelectContent>
                {availableServiceTypes.map((serviceType) => (
                  <SelectItem key={serviceType.id} value={serviceType.id}>
                    {serviceType.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            {currentServiceTypes.length === 0 ? (
              <ButtonEmpty title='No service types selected.' subtitle='' onClick={() => {}} />
            ) : (
              <div className='space-y-3'>
                {currentServiceTypes.map((serviceType) => (
                  <ServiceTypeItem
                    key={serviceType.id}
                    serviceType={serviceType}
                    onRemove={() => removeServiceType(serviceType.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
